import React, { useEffect, useState } from 'react';
import { FiCheck, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { getSettings, saveSettings, getAgencies, addAgency, type IvansSettings, type MonitoredAgency } from '../ivansMonitorService';

const SettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<IvansSettings>({ thresholdHours: 2, recipients: [], nowcertsMatchWindowHours: 6 });
  const [recipientsText, setRecipientsText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  // API Keys — deliberately tucked away behind a collapsed section, not
  // part of the main settings form. Per-agency, since a NowCerts API
  // key's token is scoped to exactly one agency (see nowcertsClient.ts) —
  // most agencies never need one set here at all.
  const [apiKeysOpen, setApiKeysOpen] = useState(false);
  const [agencies, setAgencies] = useState<MonitoredAgency[]>([]);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [newKey, setNewKey] = useState('');
  const [keySaving, setKeySaving] = useState(false);
  const [keySaved, setKeySaved] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  useEffect(() => {
    getSettings().then((s) => {
      setSettings(s);
      setRecipientsText(s.recipients.join(', '));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (apiKeysOpen && agencies.length === 0) {
      getAgencies().then((a) => {
        setAgencies(a);
        if (a.length > 0) setSelectedAgency(a[0].name);
      }).catch(() => {});
    }
  }, [apiKeysOpen, agencies.length]);

  const handleSave = async () => {
    const recipients = recipientsText.split(',').map((r) => r.trim()).filter(Boolean);
    const next = { ...settings, recipients };
    await saveSettings(next);
    setSettings(next);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 3000);
  };

  const selected = agencies.find((a) => a.name === selectedAgency);

  const handleSaveKey = async () => {
    if (!selected || !newKey.trim()) return;
    setKeySaving(true);
    setKeyError(null);
    try {
      await addAgency({
        name: selected.name,
        accountCode: selected.accountCode,
        credentialProfile: selected.credentialProfile,
        nowcertsAgencyDatabaseId: selected.nowcertsAgencyDatabaseId,
        nowcertsApiKey: newKey.trim(),
      });
      setNewKey('');
      setKeySaved(true);
      window.setTimeout(() => setKeySaved(false), 3000);
      const refreshed = await getAgencies();
      setAgencies(refreshed);
    } catch (err) {
      setKeyError(err instanceof Error ? err.message : 'Failed to save key.');
    } finally {
      setKeySaving(false);
    }
  };

  if (loading) return <div className="ca-empty">Loading…</div>;

  return (
    <div className="ivm-section">
      <div className="ivm-section__header">
        <div className="ivm-section__title">Settings</div>
      </div>

      <div className="ca-run__grid">
        <label className="ca-run__field">
          <span>IVANS delay threshold (hours)</span>
          <input
            className="ca-run__input"
            type="number"
            value={settings.thresholdHours}
            onChange={(e) => setSettings({ ...settings, thresholdHours: Number(e.target.value) })}
          />
        </label>
        <label className="ca-run__field">
          <span>NowCerts match window (hours)</span>
          <input
            className="ca-run__input"
            type="number"
            value={settings.nowcertsMatchWindowHours}
            onChange={(e) => setSettings({ ...settings, nowcertsMatchWindowHours: Number(e.target.value) })}
          />
        </label>
        <label className="ca-run__field">
          <span>Alert recipients (comma-separated — blank uses the project-wide default)</span>
          <input className="ca-run__input" value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} />
        </label>

        <button type="button" className="ca-run__btn ca-run__btn--full" onClick={handleSave}>
          <FiCheck size={13} /> Save Settings
        </button>
      </div>

      {saved && <div className="ca-run__status ca-run__status--success" style={{ marginTop: 'var(--space-2)' }}>Saved.</div>}

      <div style={{ marginTop: 'var(--space-5)', borderTop: '1px solid var(--border-glass)', paddingTop: 'var(--space-3)' }}>
        <button
          type="button"
          onClick={() => setApiKeysOpen((v) => !v)}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)',
          }}
        >
          {apiKeysOpen ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
          API Keys
        </button>

        {apiKeysOpen && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            {agencies.length === 0 ? (
              <div className="ca-empty">No agencies yet — add one on the Agencies tab first.</div>
            ) : (
              <div className="ca-run__grid">
                <label className="ca-run__field">
                  <span>Agency</span>
                  <select className="ca-run__select" value={selectedAgency} onChange={(e) => setSelectedAgency(e.target.value)}>
                    {agencies.map((a) => (
                      <option key={a.name} value={a.name}>
                        {a.name}{a.nowcertsApiKeyConfigured ? ' — key set' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="ca-run__field">
                  <span>NowCerts API Key (this agency's own — only needed if it differs from the project default)</span>
                  <input className="ca-run__input" type="password" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
                </label>
                <button type="button" className="ca-run__btn" disabled={keySaving || !newKey.trim()} onClick={handleSaveKey}>
                  <FiCheck size={13} /> Save Key
                </button>
              </div>
            )}
            {keySaved && <div className="ca-run__status ca-run__status--success" style={{ marginTop: 'var(--space-2)' }}>Key saved.</div>}
            {keyError && <div className="ca-run__status ca-run__status--error" style={{ marginTop: 'var(--space-2)' }}>{keyError}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsTab;
